import * as queries from "../../graphql/queries";
import * as mutations from "../../graphql/mutations";
import * as customQueries from "../../graphql/customQueries";
import {
  GetUserQuery,
  ListUsersQuery,
  CreateUserMutation,
  UpdateUserMutation,
  User,
  DeleteUserInput,
  DeleteUserMutation,
  ModelUserConnection,
  UsersByNPIQuery,
  SearchUsersQuery,
  SearchableUserConnection,
  SearchUsersQueryVariables,
  ProgramMembership,
  ModelProgramMembershipConnection,
  UserStatsQuery,
  UserStats,
  UpdateUserEmailArgs,
  UpdateUserEmailQuery,
  ProgramRole,
  SearchProgramMembershipsQuery,
  SearchProgramMembershipsQueryVariables,
  SendUserInvitationResult,
  SendUserInvitationQuery,
  PaginatableSiblingUsersArgs,
  PaginatableSiblingUsersQuery,
  PaginatableSiblingUsersResult,
  PaginatableSiblingUsersQueryVariables,
  SendUserInvitationQueryVariables,
  SendUserInvitationArgs,
  AssessmentRole
} from "../../API";
import { ProgramUser } from "../../custom-types";
import {
  genericQuery,
  listQueryAutoPaginated,
  searchQuery
} from "../api/apiUtils";
import { BulkOperationsSummary } from "../../app/types";
import { API } from "aws-amplify";

/**
 * Fetch all the user objects. Optionally pass in a predicate for filtering, etc.
 * @param predicate The object to use as a predicate (ex: {filter: {email: {eq: "test@fake.com"}}})
 * @returns An array of User objects or null
 */
export const fetchAllUsers = async (predicate?: object) => {
  return await listQueryAutoPaginated<
    ListUsersQuery,
    User,
    ModelUserConnection
  >(queries.listUsers, "listUsers", "items", predicate);
};

/**
 * Fetch all users with the email addressed passed in.
 * @param email The email address to return matching users for.
 * @returns  An array of User objects or null
 */
export const fetchUsersByEmail = async (
  email: string
): Promise<User[] | null> => {
  // Prefer exact lookup via GET /api/user?email=... mapped from GraphQL getUser
  const userByEmail = await genericQuery<GetUserQuery, User>(
    queries.getUser,
    "getUser",
    { email }
  );
  return userByEmail ? [userByEmail] : null;
};

export const fetchUsersByProgramIds = async (
  programIds: string[],
  role?: ProgramRole,
  activeUsersOnly = false
): Promise<ProgramUser[] | null> => {
  const filterClauses: NonNullable<
    SearchProgramMembershipsQueryVariables["filter"]
  >[] = [
    {
      or: programIds.map((pid) => {
        return { programId: { eq: pid } };
      })
    }
  ];

  if (role) {
    filterClauses.push({ programRole: { eq: role } });
  }

  if (activeUsersOnly) {
    filterClauses.push({
      or: [{ endDate: { exists: false } }, { endDate: { eq: "" } }]
    });
  }

  const variables: SearchProgramMembershipsQueryVariables = {
    filter:
      filterClauses.length === 1
        ? filterClauses[0]
        : { and: filterClauses }
  };

  const results = await listQueryAutoPaginated<
    SearchProgramMembershipsQuery,
    ProgramMembership,
    ModelProgramMembershipConnection
  >(
    queries.searchProgramMemberships,
    "searchProgramMemberships",
    "items",
    variables
  );

  const uniqueUsers: User[] = [];
  const finalResults: ProgramUser[] = [];

  if (results) {
    for (const membership of results) {
      const member = membership.programMember;
      const inUniqueUsers = uniqueUsers.find((u) => u.id === member?.id);
      if (member && !inUniqueUsers) {
        uniqueUsers.push(member);
      }
    }
    for (const user of uniqueUsers) {
      const programMemberships = results.filter(
        (r) => r.programMemberId === user.id
      );
      const roles = programMemberships.map(
        (membership) => membership.programRole
      );
      const programNames = programMemberships.map(
        (membership) => membership.program?.name
      );
      const institutionNames = programMemberships.map(
        (membership) => membership.program?.institution?.name
      );
      const entry: ProgramUser = {
        ...user,
        role: roles.join(",\r\n"),
        program: programNames.join(",\r\n"),
        institutionName: institutionNames.join(",\r\n"),
        status: user.eulaAcceptedDate ? "Active" : "Unregistered",
        programMemberships: {
          __typename: "ModelProgramMembershipConnection",
          items: programMemberships
        }
      };
      finalResults.push(entry);
    }
  }

  if (finalResults) {
    return finalResults;
  }
  return null;
};

/**
 * Fetch all users with the npi passed in.
 * @param npi The npi to return matching users for.
 * @returns An array of User objects or null
 */
export const fetchUsersByNPI = async (npi: number): Promise<User[] | null> => {
  const path = "users-by-npi";
  const resp = await API.get("simpl-api", path, {
    queryStringParameters: { npi }
  });
  const users = Array.isArray(resp) ? resp : resp?.users;
  if (!Array.isArray(users) || users.length === 0) {
    return null;
  }

  // Ensure users are hydrated with programMemberships for AddUserToProgram table columns.
  const hydratedUsers = await Promise.all(
    users.map(async (user) => {
      try {
        const fullUser = await genericQuery<GetUserQuery, User>(
          queries.getUser,
          "getUser",
          { id: user.id }
        );
        return fullUser ?? user;
      } catch {
        return user;
      }
    })
  );

  return hydratedUsers;
};

export const searchUsers = async (predicate: SearchUsersQueryVariables) => {
  return await searchQuery<SearchUsersQuery, SearchableUserConnection>(
    queries.searchUsers,
    "searchUsers",
    predicate
  );
};

export async function fetchUser(id: string) {
  const predicate = { id: id };
  return await genericQuery<GetUserQuery, User>(
    queries.getUser,
    "getUser",
    predicate
  );
}

export async function fetchUserStats(id: string) {
  const predicate = { userId: id };
  return await genericQuery<UserStatsQuery, UserStats>(
    queries.userStats,
    "userStats",
    predicate
  );
}

export type UpdateUserInput = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  npi?: number | null;
  pgy?: number | null;
  simplLearnId?: string | null;
  suffix?: string | null;
  jobTitle?: string | null;
  deviceToken?: string | null;
  devicePlatform?: string | null;
  cognitoUsername?: string | null;
};

export type UpdateUserResult = {
  success: boolean;
  updateUser: User;
  error?: string;
};

export async function updateUser(input: UpdateUserInput): Promise<User | null> {
  const path = "user";

  try {
    const response = await API.put("simpl-api", path, { body: input });

    // Response format: { success: true, updateUser: {...} } or { success: false, error: "..." }
    if (response?.success) {
      return response.updateUser as User;
    }

    const responseError = response?.error || "Failed to update user";
    throw new Error(responseError);
  } catch (caughtError: unknown) {
    const error = caughtError as {
      response?: { data?: { error?: string; message?: string } };
      message?: string;
    };
    const errorMessage =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to update user";

    throw new Error(errorMessage);
  }
}

export type CreateUserInput = {
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  suffix?: string | null;
  npi?: number | null;
  simplLearnId?: string | null;
  pgy?: number | null;
  simpl1Id?: number | null;
  jobTitle?: string | null;
  adminId?: string;
  programId: string;
  programRole: ProgramRole;
  traineeType?: string | null;
  startDate: string;
};

export type CreateUserResult = {
  success: boolean;
  userId?: string;
  programMembershipId?: string;
  error?: string;
};
export const createUser = async (
  input: CreateUserInput
): Promise<CreateUserResult> => {
  const path = "user";
  try {
    const response = (await API.post("simpl-api", path, {
      body: input
    })) as CreateUserResult;
    
    // Lambda returns { success: true, userId, programMembershipId } on success
    // or { success: false, error: string } on error
    if (response.success === false || response.error) {
      console.error("Error creating user:", response.error);
      // Return the error response so the caller can access the error message
      return {
        success: false,
        error: response.error || "Unknown error creating user"
      };
    }
    
    return response;
  } catch (error: any) {
    // Handle API errors
    console.error("Error calling create-user lambda:", error);
    // Extract error message from response if available
    let errorMessage = "Unknown error creating user";
    if (error?.response?.data) {
      console.error("Lambda error response:", error.response.data);
      // Lambda returns { success: false, error: "..." } on error
      if (error.response.data.error) {
        errorMessage = error.response.data.error;
      } else if (error.response.data.message) {
        errorMessage = error.response.data.message;
      }
    } else if (error?.message) {
      errorMessage = error.message;
    }
    
    // Return error result instead of null so caller can access the error message
    return {
      success: false,
      error: errorMessage
    };
  }
};

export const deleteUser = async (
  input: DeleteUserInput
): Promise<User | null> => {
  const predicate = {
    input: input
  };
  return await genericQuery<DeleteUserMutation, User>(
    mutations.deleteUser,
    "deleteUser",
    predicate
  );
};

export const updateUserEmail = async (
  input: UpdateUserEmailArgs
): Promise<string | null> => {
  const path = "user/email";
  const response = await API.put("simpl-api", path, {
    body: {
      userId: input.userId,
      newEmail: input.newEmail
    }
  });
  // Response format: { success: true, message: "..." } or { success: false, error: "..." }
  if (response.success) {
    return response.message;
  } else {
    throw new Error(response.error || "Failed to update user email");
  }
};

export const fetchSiblingUsers = async (
  userId: string,
  assessmentRole: AssessmentRole | null
) => {
  const args: PaginatableSiblingUsersArgs = {
    userId: userId,
    assessmentRole
  };

  const variables: PaginatableSiblingUsersQueryVariables = { input: args };
  return await listQueryAutoPaginated<
    PaginatableSiblingUsersQuery,
    User,
    PaginatableSiblingUsersResult
  >(
    customQueries.siblingUsersForCreate,
    "paginatableSiblingUsers",
    "items",
    variables
  );
};

export const bulkUpdatePgy = async (
  mutationStatements: Pick<UpdateUserInput, "id" | "pgy">[]
) => {
  const path = "user";
  const responses = await Promise.allSettled(
    mutationStatements.map(async (mutation) => {
      try {
        const response = await API.put("simpl-api", path, { body: mutation });

        // Response format: { success: true, updateUser: {...} } or { success: false, error: "..." }
        if (response?.success) {
          return response.updateUser as User;
        }

        const responseError = response?.error || "Failed to update user";
        throw new Error(responseError);
      } catch (caughtError: unknown) {
        const error = caughtError as {
          response?: { data?: { error?: string; message?: string } };
          message?: string;
        };
        const errorMessage =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update user";

        throw new Error(errorMessage);
      }
    })
  );

  const responseResults = responses.reduce((responsesSummary, result) => {
    return {
      ...responsesSummary,
      [result.status]:
        result.status in responsesSummary
          ? (responsesSummary[result.status] as number) + 1
          : 1
    };
  }, {} as BulkOperationsSummary);

  return responseResults;
};

export type ResendInvitationResult = {
  userId: string;
  success: boolean;
  error?: string;
};

export type ResendInvitationSuccessResponse = {
  success: boolean;
  successCount: number;
  total: number;
  failed: number;
  results: ResendInvitationResult[];
};

export type ResendInvitationErrorResponse = {
  success: false;
  error: string;
};

export type ResendInvitationResponse =
  | ResendInvitationSuccessResponse
  | ResendInvitationErrorResponse;

export const sendUserInvitation = async (userId: string) => {
  const path = "invites/resend";
  try {
    const response = (await API.post("simpl-api", path, {
      body: { userIds: [userId] }
    })) as ResendInvitationResponse;

    if ("error" in response) {
      throw new Error(response.error);
    }

    // Check if the single user's invitation was successful
    const userResult = response.results.find((r) => r.userId === userId);
    if (!userResult || !userResult.success) {
      const errorMsg = userResult?.error || "Failed to send invitation";
      throw new Error(errorMsg);
    }

    // Return format compatible with old GraphQL response
    return {
      success: true,
      error: null
    } as SendUserInvitationResult;
  } catch (error: unknown) {
    console.error("Error calling resend-invitations lambda:", error);
    const errorMessage =
      error instanceof Error
        ? error.message
        : "Unknown error sending invitation";

    // Return format compatible with old GraphQL response
    return {
      success: false,
      error: errorMessage
    } as SendUserInvitationResult;
  }
};

export type AvailableParticipantsInput = {
  userId: string;
  assessmentRole: AssessmentRole;
  query?: string;
  sortOrder?: "asc" | "desc";
};
export type AvailableParticipant = {
  userId: string;
  fullName: string;
  initials: string;
  sharedProgramsDisplay: string;
  email: string;
};
export const availableParticipantsAPI = async ({
  userId,
  assessmentRole,
  query,
  sortOrder
}: AvailableParticipantsInput): Promise<AvailableParticipant[]> => {
  const availableParticipants = (await API.get(
    "simpl-api",
    "available-participants",
    {
      queryStringParameters: {
        userId: userId,
        assessmentRole: assessmentRole,
        query: query,
        sortOrder: sortOrder
      }
    }
  )) as AvailableParticipant[];
  return availableParticipants;
};
